import { openDB } from 'idb';
import type { DBSchema, IDBPDatabase } from 'idb';
import type {
  Feature,
  FeatureRequest,
  Lead,
  LeadSignal,
  Objection,
  PlaybookMessage,
  Visit,
} from '../../../shared/types';
import { DB_NAME, DB_VERSION } from '../config';

export type OutboxMethod = 'POST' | 'PUT' | 'PATCH' | 'DELETE';
export type OutboxStatus = 'pending' | 'failed' | 'parked';

/** One queued REST mutation (D3). Replayed FIFO by seq. */
export interface OutboxOp {
  seq?: number;
  /** Stable op id so a retried enqueue never duplicates. */
  id: string;
  method: OutboxMethod;
  path: string;
  body?: unknown;
  /** Human label for the sync-issues UI, e.g. "Log visit · Tacos Los Primos" */
  label: string;
  created_at: string;
  attempts: number;
  status: OutboxStatus;
  last_error?: string;
  depends_on?: string[];
  local_lead_id?: string;
  existing_lead_id?: string;
  park_reason?: 'conflict' | 'dependency';
}

export interface MetaRow {
  key: string;
  value: unknown;
}

export interface CrmDB extends DBSchema {
  leads: { key: string; value: Lead; indexes: { by_stage: string; by_updated: string } };
  visits: { key: string; value: Visit; indexes: { by_lead: string; by_visited_at: string } };
  objections: { key: string; value: Objection; indexes: { by_lead: string; by_visit: string } };
  features: { key: string; value: Feature };
  feature_requests: { key: string; value: FeatureRequest; indexes: { by_feature: string; by_lead: string } };
  /** The message library (D88), mirrored so the brief attaches a draft with no network. */
  playbook: { key: string; value: PlaybookMessage; indexes: { by_persona: string } };
  /** Where each lead came from (D96): the public records attached to it, mirrored so the lead page
   *  answers "why is this a lead?" with no signal. Server-owned, like the playbook. */
  lead_signals: { key: string; value: LeadSignal; indexes: { by_lead: string } };
  outbox: { key: number; value: OutboxOp; indexes: { by_status: string } };
  meta: { key: string; value: MetaRow };
}

export type CrmDatabase = IDBPDatabase<CrmDB>;

export const openCrmDb = (): Promise<CrmDatabase> =>
  openDB<CrmDB>(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion) {
      if (oldVersion < 1) {
        const leads = db.createObjectStore('leads', { keyPath: 'id' });
        leads.createIndex('by_stage', 'stage');
        leads.createIndex('by_updated', 'updated_at');
        const visits = db.createObjectStore('visits', { keyPath: 'id' });
        visits.createIndex('by_lead', 'lead_id');
        visits.createIndex('by_visited_at', 'visited_at');
        const objections = db.createObjectStore('objections', { keyPath: 'id' });
        objections.createIndex('by_lead', 'lead_id');
        objections.createIndex('by_visit', 'visit_id');
        const outbox = db.createObjectStore('outbox', { keyPath: 'seq', autoIncrement: true });
        outbox.createIndex('by_status', 'status');
        db.createObjectStore('meta', { keyPath: 'key' });
      }
      if (oldVersion < 2) {
        // M4: watchlist mirror (features + requests) so capture works offline (D35)
        db.createObjectStore('features', { keyPath: 'id' });
        const requests = db.createObjectStore('feature_requests', { keyPath: 'id' });
        requests.createIndex('by_feature', 'feature_id');
        requests.createIndex('by_lead', 'lead_id');
      }
      if (oldVersion < 3) {
        // D88: the message library, so the daily brief can render drafts offline
        const playbook = db.createObjectStore('playbook', { keyPath: 'id' });
        playbook.createIndex('by_persona', 'persona');
      }
      if (oldVersion < 4) {
        // D96: where each lead came from, so the lead page can say it with no signal
        const provenance = db.createObjectStore('lead_signals', { keyPath: 'id' });
        provenance.createIndex('by_lead', 'lead_id');
      }
    },
    blocking() {
      // A newer version wants to open in another tab: close so the upgrade can proceed.
      dbPromise?.then((db) => db.close());
      dbPromise = undefined;
    },
  });

let dbPromise: Promise<CrmDatabase> | undefined;

export const getDb = (): Promise<CrmDatabase> => {
  if (!dbPromise) {
    // One failed open (storage pressure, a connection another tab closed) is not the answer forever:
    // the next read opens again instead of handing back the same rejection until a reload.
    const opening: Promise<CrmDatabase> = openCrmDb().catch((err: unknown) => {
      if (dbPromise === opening) dbPromise = undefined;
      throw err;
    });
    dbPromise = opening;
  }
  return dbPromise;
};

/** Tests: drop the cached connection so a fresh IDBFactory is used. */
export const resetDbConnection = async (): Promise<void> => {
  const p = dbPromise;
  dbPromise = undefined;
  if (p) (await p).close();
};
